import { useId } from 'react'

const LogoSvg = ({ className = 'solid-logo', ...rest }) => {
  const uid = useId().replace(/:/g, '')
  const id = (name) => `${name}-${uid}`
  const ref = (name) => `url(#${id(name)})`

  return (
    <svg
      className={className}
      viewBox="0 0 320 320"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="RS neural monogram"
      {...rest}
    >
      <defs>
        <linearGradient id={id('rGrad')} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1a75ff" />
          <stop offset="100%" stopColor="#002fcc" />
        </linearGradient>
        <linearGradient id={id('sGrad')} x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ff2020" />
          <stop offset="100%" stopColor="#b30000" />
        </linearGradient>
        <linearGradient id={id('linkGrad')} x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="#0066ff" />
          <stop offset="50%" stopColor="#a100ff" />
          <stop offset="100%" stopColor="#ff0000" />
        </linearGradient>
        <radialGradient id={id('coreGlow')} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="35%" stopColor="#a100ff" stopOpacity="1" />
          <stop offset="100%" stopColor="#a100ff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={id('nodeGlowRed')} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="40%" stopColor="#ff0000" stopOpacity="1" />
          <stop offset="100%" stopColor="#ff0000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={id('nodeGlowBlue')} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="40%" stopColor="#0066ff" stopOpacity="1" />
          <stop offset="100%" stopColor="#0066ff" stopOpacity="0" />
        </radialGradient>
      </defs>

      <g className="chip-frame" fill="none">
        <polygon className="chip-outer"
                 points="160,18 285,80 285,240 160,302 35,240 35,80"
                 stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        <polygon className="chip-inner"
                 points="160,32 272,88 272,232 160,288 48,232 48,88"
                 stroke="rgba(184,74,255,0.25)" strokeWidth="1" />
      </g>

      <g className="circuit-bg" strokeWidth="1" fill="none">
        <path d="M20 60 L55 60 L55 90" stroke="rgba(0,194,255,0.35)" />
        <path d="M300 80 L265 80 L265 50" stroke="rgba(255,59,59,0.35)" />
        <path d="M25 250 L60 250 L60 220" stroke="rgba(0,194,255,0.35)" />
        <path d="M295 255 L255 255 L255 230" stroke="rgba(255,59,59,0.35)" />
        <path d="M160 20 L160 45" stroke="rgba(184,74,255,0.4)" />
        <path d="M160 300 L160 275" stroke="rgba(184,74,255,0.4)" />
        <path d="M8 160 L35 160" stroke="rgba(0,194,255,0.35)" />
        <path d="M312 160 L285 160" stroke="rgba(255,59,59,0.35)" />
        <circle cx="20" cy="60" r="2" fill="rgba(0,194,255,0.7)" />
        <circle cx="300" cy="80" r="2" fill="rgba(255,59,59,0.7)" />
        <circle cx="25" cy="250" r="2" fill="rgba(0,194,255,0.7)" />
        <circle cx="295" cy="255" r="2" fill="rgba(255,59,59,0.7)" />
        <circle cx="8" cy="160" r="2" fill="rgba(0,194,255,0.7)" />
        <circle cx="312" cy="160" r="2" fill="rgba(255,59,59,0.7)" />
      </g>

      <g className="letter-r"
         stroke={ref('rGrad')}
         strokeWidth="4"
         fill="none"
         strokeLinecap="round"
         strokeLinejoin="round">
        <path className="stroke-path r-spine"
              d="M70 230 L70 80 L120 80 Q150 80 150 115 Q150 150 120 150 L70 150" />
        <path className="stroke-path r-leg"
              d="M108 150 L150 230" />
      </g>

      <g className="letter-s"
         stroke={ref('sGrad')}
         strokeWidth="4"
         fill="none"
         strokeLinecap="round"
         strokeLinejoin="round">
        <path className="stroke-path s-curve"
              d="M240 95
                 Q215 75 190 85
                 Q165 95 170 120
                 Q175 140 200 145
                 L215 150
                 Q245 158 245 185
                 Q245 215 215 220
                 Q185 225 165 205" />
      </g>

      <g className="links"
         stroke={ref('linkGrad')}
         strokeWidth="1.5"
         fill="none"
         strokeLinecap="round"
         strokeLinejoin="round">
        <path className="stroke-path link-top"
              d="M150 80 L175 80 L185 92" opacity="0.75" />
        <path className="stroke-path link-mid"
              d="M150 150 L185 150" opacity="0.85" />
        <path className="stroke-path link-bot"
              d="M150 230 L165 230 L175 215" opacity="0.75" />
      </g>

      <g className="synapse">
        <circle className="core-pulse pulse-1" cx="160" cy="160" r="6" fill="none"
                stroke="rgba(161,0,255,0.9)" strokeWidth="1.4" />
        <circle className="core-pulse pulse-2" cx="160" cy="160" r="6" fill="none"
                stroke="rgba(0,102,255,0.85)" strokeWidth="1.2" />
        <circle className="core-pulse pulse-3" cx="160" cy="160" r="6" fill="none"
                stroke="rgba(255,0,0,0.85)" strokeWidth="1.2" />
        <circle className="core-glow" cx="160" cy="160" r="16" fill={ref('coreGlow')} />
        <circle className="core-center" cx="160" cy="160" r="2.8" fill="#ffffff" />
      </g>

      <g className="nodes">
        <circle className="node node-1" cx="70" cy="80" r="5" fill={ref('nodeGlowBlue')} />
        <circle className="node node-2" cx="70" cy="150" r="5" fill={ref('nodeGlowBlue')} />
        <circle className="node node-3" cx="70" cy="230" r="5" fill={ref('nodeGlowBlue')} />
        <circle className="node node-4" cx="150" cy="115" r="4" fill={ref('nodeGlowBlue')} />
        <circle className="node node-5" cx="150" cy="230" r="5" fill={ref('nodeGlowBlue')} />

        <circle className="node node-6" cx="240" cy="95" r="5" fill={ref('nodeGlowRed')} />
        <circle className="node node-7" cx="170" cy="120" r="4" fill={ref('nodeGlowRed')} />
        <circle className="node node-8" cx="245" cy="185" r="4" fill={ref('nodeGlowRed')} />
        <circle className="node node-9" cx="165" cy="205" r="5" fill={ref('nodeGlowRed')} />
      </g>

      <g className="signals">
        <circle className="signal" r="3" fill="#1a9dff">
          <animateMotion dur="3.2s" repeatCount="indefinite" begin="2.2s">
            <mpath href={`#${id('motion-r')}`} />
          </animateMotion>
        </circle>
        <circle className="signal" r="3" fill="#ff1a1a">
          <animateMotion dur="3.8s" repeatCount="indefinite" begin="2.6s">
            <mpath href={`#${id('motion-s')}`} />
          </animateMotion>
        </circle>
        <circle className="signal" r="2" fill="#ffffff">
          <animateMotion dur="4.2s" repeatCount="indefinite" begin="3.4s">
            <mpath href={`#${id('motion-bridge')}`} />
          </animateMotion>
        </circle>
      </g>

      <path id={id('motion-r')}
            d="M70 230 L70 80 L120 80 Q150 80 150 115 Q150 150 120 150 L70 150"
            fill="none" stroke="none" />
      <path id={id('motion-s')}
            d="M240 95 Q215 75 190 85 Q165 95 170 120 Q175 140 200 145 L215 150 Q245 158 245 185 Q245 215 215 220 Q185 225 165 205"
            fill="none" stroke="none" />
      <path id={id('motion-bridge')}
            d="M150 150 L185 150 L170 120"
            fill="none" stroke="none" />
    </svg>
  )
}

export default LogoSvg
